import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import RegisterUi from "./RegisterUi/RegisterUi";
import { useNavigate, useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { BASE_URL } from "service";

// ✅ Email and mobile validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^\d{10}$/;

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Manage all form fields in a single object
  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    mobileNumber: "",
    password: "",
    referralCode: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  // ✅ Destructure form fields for easier access
  const { email, fullName, mobileNumber, password, referralCode } = formData;

  // ✅ Handle Input Change
  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "email" ? value.toLowerCase() : value, // Always lowercase email
    }));
  }, []);

  // ✅ Extract referral code from URL (only once)
  useEffect(() => {
    const r_code = new URLSearchParams(location.search).get("r_code");
    if (r_code) setFormData((prev) => ({ ...prev, referralCode: r_code }));
  }, [location.search]);

  // ✅ Validation Helper
  const validateInputs = () => {
    if (!EMAIL_REGEX.test(email)) {
      alert("Please enter a valid email address (e.g., example@domain.com).");
      return false;
    }
    if (!MOBILE_REGEX.test(mobileNumber)) {
      alert("Mobile number must be exactly 10 digits.");
      return false;
    }
    if (!fullName.trim() || !password.trim()) {
      alert("Full name and password are required.");
      return false;
    }
    return true;
  };

  // ✅ Main Registration Logic
  const handleRegister = async () => {
    if (!validateInputs()) return;

    try {
      setIsLoading(true);

      // Step 1: Register User
      const { data: registerData } = await axios.post(`${BASE_URL}/api/users/register`, {
        first_name: fullName,
        last_name: "LastName",
        email: email.toLowerCase(),
        mobile_one: mobileNumber,
        password,
        referral_code: referralCode || null,
      });

      console.log("✅ Registration successful:", registerData);

      // Step 2: Generate Referral Code
      const { data: referralData } = await axios.post(
        `${BASE_URL}/api/referral/generate/?email=${email.toLowerCase()}`
      );

      const newReferralCode = referralData?.referral?.referral_code;
      if (newReferralCode) {
        localStorage.setItem("referral_code", newReferralCode);
      }

      navigate("/login");
    } catch (error) {
      const message =
        error.response?.data?.message || "An unexpected error occurred while registering.";
      console.error("❌ Registration failed:", message);
      alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <RegisterUi
      email={email}
      setEmail={(value) => handleChange("email", value)}
      fullName={fullName}
      setFullName={(value) => handleChange("fullName", value)}
      mobileNumber={mobileNumber}
      setMobileNumber={(value) => handleChange("mobileNumber", value)}
      password={password}
      setPassword={(value) => handleChange("password", value)}
      referralCode={referralCode}
      setReferralCode={(value) => handleChange("referralCode", value)}
      onRegister={handleRegister}
      isLoading={isLoading}
    />
  );
};

export default Register;
